import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "./ui/switch";
import { useLanguage } from "../i18n/LanguageContext";

export const openCookieSettings = () => window.dispatchEvent(new Event("aw-open-cookies"));

const Category = ({ id, title, desc, checked, onChange, disabled }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-t border-black/10">
    <div>
      <div className="font-sans-aw text-sm text-black">{title}</div>
      <p className="font-sans-aw text-xs text-black/60 mt-1 leading-relaxed">{desc}</p>
    </div>
    <Switch data-testid={`cookie-toggle-${id}`} checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={title} />
  </div>
);

export const CookieBanner = () => {
  const { t } = useLanguage();
  const stored = window.awConsent?.read();
  const [open, setOpen] = useState(() => !stored);
  const [details, setDetails] = useState(false);
  const [choice, setChoice] = useState({ statistics: !!stored?.statistics, marketing: !!stored?.marketing });

  useEffect(() => {
    const show = () => {
      const c = window.awConsent?.read();
      setChoice({ statistics: !!c?.statistics, marketing: !!c?.marketing });
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener("aw-open-cookies", show);
    return () => window.removeEventListener("aw-open-cookies", show);
  }, []);

  if (!open) return null;

  const save = (c) => {
    setOpen(false);
    window.awConsent.save(c);
  };

  const c = t.cookies;
  const btn = "flex-1 px-4 py-3 font-sans-aw text-[11px] tracking-luxury uppercase transition-colors duration-300 border";

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-label={c.title}
      className="fixed bottom-0 left-0 right-0 md:left-8 md:bottom-8 md:right-auto md:max-w-md z-[60] bg-white text-black border border-black/15 p-5 md:p-8 shadow-[0_10px_40px_rgba(0,0,0,0.12)] max-h-[85vh] overflow-y-auto"
    >
      <h4 className="font-serif-aw text-xl">{c.title}</h4>
      <p className="font-sans-aw text-sm text-black/70 mt-3 leading-relaxed">
        {c.text}{" "}
        <Link data-testid="cookie-banner-more" to="/datenschutz" className="underline underline-offset-4">{c.more}</Link>
      </p>

      {details && (
        <div data-testid="cookie-details" className="mt-5">
          <Category id="necessary" title={c.necessary_title} desc={c.necessary_desc} checked disabled />
          <Category id="statistics" title={c.statistics_title} desc={c.statistics_desc} checked={choice.statistics} onChange={(v) => setChoice({ ...choice, statistics: v })} />
          <Category id="marketing" title={c.marketing_title} desc={c.marketing_desc} checked={choice.marketing} onChange={(v) => setChoice({ ...choice, marketing: v })} />
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button data-testid="cookie-accept-btn" onClick={() => save({ statistics: true, marketing: true })} className={`${btn} bg-black text-white border-black hover:bg-white hover:text-black`}>
          {c.accept}
        </button>
        <button data-testid="cookie-decline-btn" onClick={() => save({ statistics: false, marketing: false })} className={`${btn} bg-white text-black border-black/30 hover:border-black`}>
          {c.decline}
        </button>
      </div>
      {details ? (
        <button data-testid="cookie-save-btn" onClick={() => save(choice)} className={`${btn} w-full mt-3 bg-white text-black border-black/30 hover:border-black`}>
          {c.save}
        </button>
      ) : (
        <button data-testid="cookie-settings-btn" onClick={() => setDetails(true)} className="mt-4 font-sans-aw text-xs text-black/60 underline underline-offset-4 hover:text-black">
          {c.settings}
        </button>
      )}
    </div>
  );
};
